import { createSeededRng } from '../../domain/seededRandom.js';
import { TRAIT_SEEDS } from '../../db/traits.js';
import type { CharacterAppearance, CharacterDraft } from '../../domain/types.js';
import type { Nature, Npc, NurtureTrait } from '../../types.js';

/** Local id helper — avoids runtime/spawn.ts import (spawn.ts not allow-listed). */
export function localUid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** UI-layer body snapshot preserved through launch when v2 draft fields are incomplete. */
export interface CharacterBodySnapshot {
  species: string;
  sex: 'M' | 'F';
  ageRange: string;
  palette: Npc['palette'];
  nature: Nature;
  nurture: NurtureTrait[];
  /** Style labels from v2 appearance for ActorContext compilation. */
  appearanceStyles?: { topStyle: string; bottomStyle: string; hairStyle: string };
}

const PALETTES: Npc['palette'][] = [
  { skin: '#f1c27d', shirt: '#4cc9f0', pants: '#3a506b', hair: '#3b2f2f' },
  { skin: '#e0ac69', shirt: '#f72585', pants: '#22223b', hair: '#111111' },
  { skin: '#8d5524', shirt: '#80ed99', pants: '#5c5c5c', hair: '#0d0d0d' },
  { skin: '#ffdbac', shirt: '#ff9e00', pants: '#6d6875', hair: '#b08968' },
];

function appearanceToPalette(appearance: CharacterAppearance): Npc['palette'] {
  return {
    skin: appearance.skinColor,
    shirt: appearance.topColor,
    pants: appearance.bottomColor,
    hair: appearance.hairColor,
  };
}

function seededNature(seed: string): Nature {
  const rng = createSeededRng(seed);
  const v = (): number => 3 + Math.floor(rng() * 6);
  return {
    sociability: v(), agreeableness: v(), emotionality: v(), caution: v(),
    discipline: v(), dominance: v(), empathy: v(), curiosity: v(), drive: v(),
    sexual_drive: v(), casual_sex_openness: v(),
  };
}

function seededTrait(seed: string): NurtureTrait {
  const rng = createSeededRng(`${seed}:trait`);
  const [trait_key] = TRAIT_SEEDS[Math.floor(rng() * TRAIT_SEEDS.length)]!;
  return {
    trait_key,
    strength: 3 + Math.floor(rng() * 6),
    reason: 'Shaped by life in this world.',
  };
}

/** Build body sidecar from v2 CharacterDraft when appearance+nature are set. */
export function bodyFromCharacterDraft(
  character: CharacterDraft,
  species = character.species ?? 'Human',
  sex: 'M' | 'F' = character.sex ?? 'M',
): CharacterBodySnapshot | null {
  if (!character.appearance || !character.nature) return null;
  return {
    species,
    sex,
    ageRange: character.ageRange ?? 'Adult',
    palette: appearanceToPalette(character.appearance),
    nature: { ...character.nature },
    nurture: character.traits.map((t) => ({
      trait_key: t.traitKey,
      strength: t.strength,
      reason: t.reason,
    })),
    appearanceStyles: {
      topStyle: character.appearance.topStyle,
      bottomStyle: character.appearance.bottomStyle,
      hairStyle: character.appearance.hairStyle,
    },
  };
}

/** @deprecated Legacy v1 meta fallback — prefer bodyFromCharacterDraft. */
export function defaultBodyForMeta(
  draftId: string,
  meta: { draftCharacterId: string; species: string; sex: 'M' | 'F'; ageRange: string },
): CharacterBodySnapshot {
  const seed = `${draftId}:${meta.draftCharacterId}:${meta.species}:${meta.sex}:${meta.ageRange}`;
  const rng = createSeededRng(seed);
  return {
    species: meta.species,
    sex: meta.sex,
    ageRange: meta.ageRange,
    palette: PALETTES[Math.floor(rng() * PALETTES.length)]!,
    nature: seededNature(seed),
    nurture: [seededTrait(seed)],
  };
}

export function bodyFromNpc(npc: Npc, meta: { species: string; sex: 'M' | 'F'; ageRange: string }): CharacterBodySnapshot {
  return {
    species: meta.species,
    sex: meta.sex,
    ageRange: meta.ageRange,
    palette: { ...npc.palette },
    nature: { ...npc.nature },
    nurture: npc.nurture.map((t) => ({ ...t })),
  };
}

export function syncBodyFromMetaEdit(
  body: CharacterBodySnapshot,
  meta: { species: string; sex: 'M' | 'F'; ageRange: string },
): CharacterBodySnapshot {
  return { ...body, species: meta.species, sex: meta.sex, ageRange: meta.ageRange };
}

export function npcFromCharacterParts(
  character: CharacterDraft,
  body: CharacterBodySnapshot,
  index: number,
  worldId: string,
): Npc {
  const displayLast = character.basic.lastInitial ? `${character.basic.lastInitial}.` : '—';
  return {
    id: localUid(`world_${worldId}_${index}`),
    firstName: character.basic.firstName,
    lastName: displayLast,
    palette: { ...body.palette },
    nature: { ...body.nature },
    nurture: body.nurture.map((t) => ({ ...t })),
    state: { mood: 'neutral', stress: 3, energy: 6 },
    savedFavorite: false,
    origin: character.source === 'user' ? 'human' : 'offline',
    neurotic: Math.random() < 0.5,
  };
}
